import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react'
import Autosuggest, { RenderInputComponentProps } from 'react-autosuggest'

import { FormFieldInputProps } from '../FormFieldInput'

import * as SC from './styled'

export type FormFieldAutoSuggestValue = {
  label: string
  value?: string
}

export type FormFieldAutoSuggestProps = Omit<
  FormFieldInputProps,
  'onSubmit'
> & {
  suggestions?: (FormFieldAutoSuggestValue | string)[]
  loadSuggestions?: (value: string) => void
  onlySuggestion?: boolean
  onSubmit?: (value: FormFieldAutoSuggestValue | string) => void
  onSubmitWithoutSuggestion?: () => void
  onWriting?: () => void
}

const FormFieldAutoSuggest: FC<FormFieldAutoSuggestProps> = ({
  className,
  suggestions,
  onChange,
  onSubmit,
  onSubmitWithoutSuggestion,
  onWriting,
  name,
  value,
  onlySuggestion,
  loadSuggestions,
  ...extraProps
}) => {
  const ref = useRef<any>(null)
  const [isHighlighted, setIsHighlighted] = useState<boolean>(false)
  const [onlySuggestionValue, setOnlySuggestionValue] = useState<string>(value)

  useEffect(() => {
    setOnlySuggestionValue(value)
  }, [value])

  return (
    <SC.AutoSuggest className={className}>
      {/* @ts-ignore */}
      <Autosuggest
        inputProps={{
          ...(onlySuggestion
            ? {
                onChange: (e) =>
                  setOnlySuggestionValue((e.target as any).value),
                value: onlySuggestionValue ?? value,
              }
            : { onChange: onChange as any, value: (value as string) ?? '' }),
        }}
        multiSection={false}
        suggestions={suggestions ?? []}
        ref={ref}
        onSuggestionHighlighted={(props) => {
          setIsHighlighted(!!props.suggestion)
        }}
        renderInputComponent={(p: RenderInputComponentProps) => {
          const { ref, key, ...otherParams } =
            p as RenderInputComponentProps & {
              key?: React.Key
            }
          return (
            <SC.Input
              key={key}
              name={name}
              {...extraProps}
              {...otherParams}
              onRef={(refValue: any) => (ref as any)(refValue)}
              onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                p?.onKeyDown?.(e)

                onWriting?.()

                if (e.key === 'Enter' && !isHighlighted) {
                  onSubmitWithoutSuggestion?.()
                }
              }}
              theme={undefined}
            />
          )
        }}
        renderSuggestion={(entry) => {
          return <div>{typeof entry === 'string' ? entry : entry?.label}</div>
        }}
        onSuggestionSelected={(_event, { suggestion, suggestionValue }) => {
          onChange?.({
            target: {
              name,
              value: suggestionValue,
            },
          } as ChangeEvent<any>)
          onSubmit?.(suggestion)
          setOnlySuggestionValue(suggestionValue)
        }}
        onSuggestionsFetchRequested={({ value: inputValue }) =>
          loadSuggestions?.(inputValue)
        }
        onSuggestionsClearRequested={() => loadSuggestions?.('')}
        getSuggestionValue={(entry): string => {
          return typeof entry === 'string' ? entry : entry?.label
        }}
      />
    </SC.AutoSuggest>
  )
}

export default FormFieldAutoSuggest
